import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Slider } from "../../components/ui/slider";
import { Upload, CheckCircle, Camera, RotateCw, AlertCircle, ZoomIn, ZoomOut, Move } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export default function UploadPage() {
  const { templateId } = useParams();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userName, setUserName] = useState("");
  const [userContact, setUserContact] = useState("");
  const [template, setTemplate] = useState(null);
  const [templateError, setTemplateError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Image adjustment state
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [rotation, setRotation] = useState(0);
  
  // Canvas refs
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Validate template exists on page load
  useEffect(() => {
    const validateTemplate = async () => {
      try {
        const response = await axios.get(`${API}/templates/${templateId}`);
        setTemplate(response.data);
        setTemplateError(null);
      } catch (error) {
        if (error.response?.status === 404) {
          setTemplateError("This photo booth link is invalid or has expired. Please contact the event organizer for a new QR code.");
        } else {
          setTemplateError("Unable to load photo booth. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };
    
    validateTemplate();
  }, [templateId]);
  
  // Load image when preview changes
  useEffect(() => {
    if (preview) {
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        drawCanvas();
      };
      img.src = preview;
    }
  }, [preview]);
  
  // Redraw canvas when adjustments change
  useEffect(() => {
    drawCanvas();
  }, [scale, offsetX, offsetY, rotation]);
  
  // Get shape dimensions based on template type
  const getShapeSize = () => {
    const templateType = template?.template_type || 'circle';
    
    // Canvas display size (pixels)
    if (templateType === 'circle') {
      return { width: 250, height: 250, type: 'circle' };
    } else if (templateType === 'circle-small') {
      // 1.5" circle (smaller display)
      return { width: 200, height: 200, type: 'circle' };
    } else if (templateType === 'rectangle') {
      // 3.3" x 5.5" ratio
      return { width: 180, height: 300, type: 'rectangle' };
    } else if (templateType === 'single') {
      // 4" x 6" ratio
      return { width: 200, height: 300, type: 'rectangle' };
    } else if (templateType === 'branded') {
      // 2.25" x 3.25" ratio
      return { width: 180, height: 260, type: 'rectangle' };
    } else if (templateType === 'postcard') {
      // 3.5" x 6.5" ratio
      return { width: 175, height: 325, type: 'rectangle' };
    }
    return { width: 250, height: 250, type: 'circle' };
  };
  
  // Draw the canvas with image and shape mask
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    
    const ctx = canvas.getContext('2d');
    const shape = getShapeSize();
    
    canvas.width = shape.width;
    canvas.height = shape.height;
    
    // Clear canvas
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, shape.width, shape.height);
    
    // Save context for clipping
    ctx.save();
    
    // Create clipping path based on shape
    ctx.beginPath();
    if (shape.type === 'circle') {
      ctx.arc(shape.width / 2, shape.height / 2, shape.width / 2, 0, Math.PI * 2);
    } else {
      ctx.rect(0, 0, shape.width, shape.height);
    }
    ctx.clip();
    
    // Fill background white
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, shape.width, shape.height);
    
    // Calculate image dimensions
    let imgWidth = img.width;
    let imgHeight = img.height;
    
    // Handle rotation - swap dimensions if rotated 90 or 270
    if (rotation === 90 || rotation === 270) {
      [imgWidth, imgHeight] = [imgHeight, imgWidth];
    }
    
    // Calculate base scale to fit image in shape
    const baseScale = Math.min(shape.width / imgWidth, shape.height / imgHeight);
    const finalScale = baseScale * scale;
    
    const drawWidth = imgWidth * finalScale;
    const drawHeight = imgHeight * finalScale;
    
    // Calculate position with offsets
    const drawX = (shape.width - drawWidth) / 2 + (offsetX / 100) * shape.width;
    const drawY = (shape.height - drawHeight) / 2 + (offsetY / 100) * shape.height;
    
    // Apply rotation around center
    ctx.translate(shape.width / 2, shape.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-shape.width / 2, -shape.height / 2);
    
    // Draw image
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    
    ctx.restore();
    
    // Draw shape border
    ctx.strokeStyle = '#9333ea';
    ctx.lineWidth = 3;
    ctx.beginPath();
    if (shape.type === 'circle') {
      ctx.arc(shape.width / 2, shape.height / 2, shape.width / 2 - 2, 0, Math.PI * 2);
    } else {
      ctx.rect(2, 2, shape.width - 4, shape.height - 4);
    }
    ctx.stroke();
  };
  
  // Handle mouse/touch drag for panning
  const handleMouseDown = (e) => {
    if (!imageRef.current) return;
    setIsDragging(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    setDragStart({ x, y, startOffsetX: offsetX, startOffsetY: offsetY });
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const shape = getShapeSize();
    const deltaX = ((x - dragStart.x) / shape.width) * 100;
    const deltaY = ((y - dragStart.y) / shape.height) * 100;
    
    setOffsetX(dragStart.startOffsetX + deltaX);
    setOffsetY(dragStart.startOffsetY + deltaY);
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Function to auto-download PDF
  const downloadPdf = async (imageId, sequenceNumber) => {
    try {
      const response = await axios.get(`${API}/images/${imageId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sequenceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setSuccess(false);
      
      // Reset adjustments
      setScale(1);
      setOffsetX(0);
      setOffsetY(0);
      setRotation(0);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a photo first");
      return;
    }

    if (!userName.trim()) {
      alert("Please enter your name");
      return;
    }

    if (!userContact.trim()) {
      alert("Please enter your contact");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_name", userName);
    formData.append("user_contact", userContact);
    formData.append("rotation", rotation.toString());
    formData.append("scale", scale.toString());
    formData.append("offset_x", offsetX.toString());
    formData.append("offset_y", offsetY.toString());

    try {
      const response = await axios.post(`${API}/upload/${templateId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const { image_id, sequence_number } = response.data;
      
      setSuccess(true);
      
      // Auto-download PDF with sequence number as filename
      await downloadPdf(image_id, sequence_number);
      
      setTimeout(() => {
        setFile(null);
        setPreview(null);
        setSuccess(false);
        setUserName("");
        setUserContact("");
        setScale(1);
        setOffsetX(0);
        setOffsetY(0);
        setRotation(0);
      }, 3000);
    } catch (error) {
      if (error.response?.status === 507) {
        alert("Out of memory. Please try with a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert("Failed to upload photo. Please try with a smaller image.");
      }
    } finally {
      setUploading(false);
    }
  };
  
  const getShapeLabel = () => {
    const templateType = template?.template_type || 'circle';
    if (templateType === 'circle') return 'Circle (2")';
    if (templateType === 'circle-small') return 'Circle (1.5")';
    if (templateType === 'rectangle') return 'Rectangle';
    if (templateType === 'single') return '4×6 Photo';
    if (templateType === 'branded') return 'Branded Card';
    if (templateType === 'postcard') return 'Postcard';
    return 'Shape';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 flex items-center justify-center p-4">
      {loading ? (
        <Card className="w-full max-w-md border-0 shadow-2xl backdrop-blur-sm bg-white/95">
          <CardContent className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading photo booth...</p>
          </CardContent>
        </Card>
      ) : templateError ? (
        <Card className="w-full max-w-md border-0 shadow-2xl backdrop-blur-sm bg-white/95" data-testid="error-card">
          <CardContent className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Photo Booth Not Found</h2>
            <p className="text-gray-600">{templateError}</p>
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-md border-0 shadow-2xl backdrop-blur-sm bg-white/95" data-testid="upload-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
            <Camera className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-600 to-purple-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Upload Your Photo
          </CardTitle>
          <CardDescription className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
            Adjust your photo to fit the {getShapeLabel().toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!success ? (
            <>
              {/* Name and Contact inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    data-testid="user-name-input"
                    className="w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Contact <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Phone/Email"
                    value={userContact}
                    onChange={(e) => setUserContact(e.target.value)}
                    data-testid="user-contact-input"
                    className="w-full text-sm"
                  />
                </div>
              </div>
              
              {/* Hidden file inputs */}
              <Input
                id="camera-upload"
                type="file"
                accept="image/*"
                capture="environment"
                data-testid="camera-upload-input"
                onChange={handleFileChange}
                className="hidden"
                disabled={!userName.trim() || !userContact.trim()}
              />
              <Input
                id="gallery-upload"
                type="file"
                accept="image/*"
                data-testid="gallery-upload-input"
                onChange={handleFileChange}
                className="hidden"
                disabled={!userName.trim() || !userContact.trim()}
              />
              
              {!preview ? (
                /* Upload buttons when no image selected */
                <>
                  {(!userName.trim() || !userContact.trim()) ? (
                    <div className="text-center py-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                      <p className="text-gray-500 text-sm">Please enter your name and contact first</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <label htmlFor="camera-upload" className="cursor-pointer">
                        <div className="border-2 border-dashed border-pink-300 rounded-xl p-4 text-center bg-pink-50/50 hover:bg-pink-100/50 transition-all hover:scale-105">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center shadow-lg">
                              <Camera className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-pink-700">Take Photo</p>
                              <p className="text-xs text-pink-500">Open Camera</p>
                            </div>
                          </div>
                        </div>
                      </label>
                      
                      <label htmlFor="gallery-upload" className="cursor-pointer">
                        <div className="border-2 border-dashed border-purple-300 rounded-xl p-4 text-center bg-purple-50/50 hover:bg-purple-100/50 transition-all hover:scale-105">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                              <Upload className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-purple-700">Choose Photo</p>
                              <p className="text-xs text-purple-500">From Gallery</p>
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                </>
              ) : (
                /* Shape preview with adjustments when image selected */
                <div className="space-y-3" data-testid="photo-preview">
                  {/* Shape Preview Canvas */}
                  <div className="flex justify-center">
                    <div 
                      className="relative bg-gray-100 rounded-xl p-3 shadow-inner"
                      style={{ display: 'inline-block' }}
                    >
                      <p className="text-xs text-center text-gray-500 mb-2">
                        Drag to position • {getShapeLabel()} Preview
                      </p>
                      <canvas
                        ref={canvasRef}
                        className="cursor-move rounded-lg shadow-lg"
                        style={{ 
                          touchAction: 'none',
                          maxWidth: '100%'
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleMouseDown}
                        onTouchMove={handleMouseMove}
                        onTouchEnd={handleMouseUp}
                        data-testid="shape-preview-canvas"
                      />
                    </div>
                  </div>
                  
                  {/* Adjustment Controls */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                    {/* Zoom Slider */}
                    <div className="flex items-center gap-2">
                      <ZoomOut className="w-4 h-4 text-gray-400" />
                      <Slider
                        value={[scale]}
                        onValueChange={(v) => setScale(v[0])}
                        min={0.5}
                        max={3}
                        step={0.05}
                        className="flex-1"
                        data-testid="zoom-slider"
                      />
                      <ZoomIn className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500 w-12 text-right">{(scale * 100).toFixed(0)}%</span>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setRotation((rotation + 90) % 360)}
                        className="flex-1"
                        data-testid="rotate-btn"
                      >
                        <RotateCw className="w-4 h-4 mr-1" />
                        Rotate
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setScale(1);
                          setOffsetX(0);
                          setOffsetY(0);
                          setRotation(0);
                        }}
                        className="flex-1"
                        data-testid="reset-btn"
                      >
                        Reset
                      </Button>
                      <label htmlFor="gallery-upload" className="flex-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          asChild
                        >
                          <span>Change</span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-center text-gray-500">JPG, PNG, HEIC - Under 5MB</p>

              <Button
                onClick={handleUpload}
                disabled={uploading || !file}
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white py-5 text-base font-semibold shadow-lg"
                data-testid="upload-button"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload & Download PDF
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="text-center py-8" data-testid="success-message">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-4 shadow-xl">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-green-600 mb-2">Success!</h3>
              <p className="text-gray-600">Your photo has been uploaded and your PDF is downloading.</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
